import { MarketingBoardAPI } from '@business/api/marketingBoard';
import { OrganizationAPI } from '@business/api/organization';
import { UserId } from '@business/entities/account';
import { PageQuery } from '@business/entities/common';
import {
  CompactMarketingBoardResponse,
  MarketingBoardId,
  MarketingBoardResponse,
  MBNoteId,
  MBNoteResponse,
  MBServiceId,
  MBServiceResponse,
  MBServiceSectionResponse,
  MBServiceStepResponse,
  MBSubscriptionId,
  MBSubscriptionResponse,
  SubscriptionBoardId,
} from '@business/entities/marketing';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { R } from '@core/utils/r';
import { Repository } from '@core/utils/repository';
import { toMoment } from '@core/utils/time';
import { Session } from '@modules/auth/session';
import { showDeleteConfirmation } from '@modules/common';
import { of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { MBServiceStepFeedback } from './../../entities/marketing/MarketingBoardResponse';
import { ProjectStepResponse } from '@business/entities/project';

export class MarketingBoardBloc extends BaseBloc {
  private readonly marketingBoardRepo = new Repository<MarketingBoardResponse | CompactMarketingBoardResponse>({
    getItemId: board => board.id,
  });

  readonly boards$ = this.marketingBoardRepo.items$;
  readonly selectBoard = this.marketingBoardRepo.selectItem;

  onReset() {
    this.marketingBoardRepo.reset();
  }

  fetchMarketingBoards = (query: PageQuery) => {
    return MarketingBoardAPI.fetchMarketingBoards(query).pipe(
      tap(data => {
        this.marketingBoardRepo.reset(data.data);
      }),
      handleMessage({ error: requestMessage('fetch_marketing_boards_error') }),
    );
  };

  fetchMarketingBoard = (id: MarketingBoardId, subBoardId: SubscriptionBoardId) => {
    return MarketingBoardAPI.fetchMarketingBoard(id, subBoardId).pipe(
      this.marketingBoardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_marketing_boards_error') }),
    );
  };

  fetchQuickMarketingBoard = (id: MarketingBoardId, subBoardId: SubscriptionBoardId) => {
    return MarketingBoardAPI.fetchQuickMarketingBoard(id, subBoardId).pipe(
      this.marketingBoardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_marketing_boards_error') }),
    );
  };

  postSetting(id: MarketingBoardId, subId: MBSubscriptionId, description: string) {
    return MarketingBoardAPI.postSetting(subId, description).pipe(
      tap(setSubscription => this.updateSubscription(id, setSubscription)),
      handleMessage({
        type: requestMessage('save_marketing_board_settings'),
      }),
    );
  }

  toggleStep = (
    boardId: MarketingBoardId,
    subscriptionId: MBSubscriptionId,
    serviceId: MBServiceId,
    sectionId: string,
    stepId: string,
    isSetup: boolean,
    feedback?: MBServiceStepFeedback,
    notes?: string,
  ) => {
    return of(null).pipe(
      tap(() => {
        const marketBoard = this.marketingBoardRepo.getItem(boardId)! as MarketingBoardResponse;
        const step = this.getStep(marketBoard, subscriptionId, serviceId, sectionId, stepId, isSetup);
        step.isCompleted = !step.isCompleted;

        if (step.isCompleted) {
          step.completedAt = toMoment().unix();
          step.completedBy = Session.user;
        } else {
          step.completedAt = null;
          step.completedBy = null;
        }

        if (feedback) {
          step.feedback = feedback;
        }
        step.notes = notes;
        this.updateStep(marketBoard, subscriptionId, serviceId, sectionId, step, isSetup);
      }),
      switchMap(() => MarketingBoardAPI.toggleStep(stepId, feedback, notes)),
      tap(updatedStep => {
        const marketBoard = this.marketingBoardRepo.getItem(boardId)! as MarketingBoardResponse;
        this.updateStep(marketBoard, subscriptionId, serviceId, sectionId, updatedStep, isSetup);
      }),
    );
  };

  createNote = (id: MarketingBoardId, data: Partial<MBNoteResponse>) => {
    return MarketingBoardAPI.createNote(id, data).pipe(
      tap(newNote => this.inNoteUpdate(id, newNote, 'insert')),
      handleMessage({
        type: requestMessage('create_marketing_board_note'),
      }),
    );
  };

  updateNote = (boardId: MarketingBoardId, id: MBNoteId, data: Partial<MBNoteResponse>) => {
    return MarketingBoardAPI.updateNote(id, data).pipe(
      tap(updatedNote => this.inNoteUpdate(boardId, updatedNote, 'update')),
      handleMessage({
        type: requestMessage('update_marketing_board_note'),
      }),
    );
  };

  deleteNote = (boardId: MarketingBoardId, id: MBNoteId) => {
    return showDeleteConfirmation('Delete Note', 'Do you really want to remove this note?').pipe(
      switchMap(() =>
        MarketingBoardAPI.deleteNote(id).pipe(
          tap(() => this.inNoteUpdate(boardId, id, 'delete')),
          handleMessage({
            type: requestMessage('delete_marketing_board_note'),
          }),
        ),
      ),
    );
  };

  toggleOverride = (boardId: MarketingBoardId, subId: MBSubscriptionId, serviceId: MBServiceId, enable: boolean) => {
    return MarketingBoardAPI.toggleOverride(subId, serviceId, enable).pipe(
      tap(newService => this.updateService(boardId, subId, newService)),
      handleMessage({
        type: requestMessage('save_marketing_board_settings'),
      }),
    );
  };

  boardCall = (boardId: MarketingBoardId, type: 'scheduleCall' | 'completedCall', forDate: number) => {
    return MarketingBoardAPI.boardCall(boardId, type, forDate).pipe(
      this.marketingBoardRepo.ops.updateOne(() => ({
        id: boardId,
        changes: item => ({
          callScheduleDate: type === 'scheduleCall' ? forDate : item.callScheduleDate,
          callCompletedDate: type === 'completedCall' ? forDate : item.callCompletedDate,
        }),
      })),
      handleMessage({
        type: requestMessage('save_marketing_board_settings'),
      }),
    );
  };

  updateManager = (boardId: MarketingBoardId, manager: UserId) => {
    return MarketingBoardAPI.updateManager(boardId, manager).pipe(
      this.marketingBoardRepo.ops.updateOne(manager => ({
        id: boardId,
        changes: () => ({
          manager,
        }),
      })),
      handleMessage({
        type: requestMessage('save_marketing_board_settings'),
      }),
    );
  };

  updateSubscription = (boardId: MarketingBoardId, newSubscription: MBSubscriptionResponse) => {
    const marketBoard = this.marketingBoardRepo.getItem(boardId)! as MarketingBoardResponse;
    const newSubs = marketBoard.subscriptions.map(sub => {
      if (sub.id === newSubscription.id) {
        return newSubscription;
      }
      return sub;
    });
    this.marketingBoardRepo.updateOne({
      id: boardId,
      changes: () => ({ subscriptions: newSubs }),
    });
  };

  updateService = (boardId: MarketingBoardId, subId: MBSubscriptionId, newService: MBServiceResponse) => {
    const marketBoard = this.marketingBoardRepo.getItem(boardId)! as MarketingBoardResponse;
    const newSubs = marketBoard.subscriptions.map(sub => {
      if (sub.id === subId) {
        return {
          ...sub,
          services: sub.services.map(s => {
            if (s.id === newService.id) {
              return newService;
            }
            return s;
          }),
        };
      }
      return sub;
    });
    this.marketingBoardRepo.updateOne({
      id: boardId,
      changes: () => ({ subscriptions: newSubs }),
    });
  };

  resetChecklists = (boardId: MarketingBoardId, subId: MBSubscriptionId) => {
    return showDeleteConfirmation(
      'Reset Checklists',
      'Do you really want to reset all checklists for this subscription? This will reset setup checklists and this month checklists for any services included.',
      { btnLabel: 'Reset' },
    ).pipe(
      switchMap(() => MarketingBoardAPI.resetChecklist(subId)),
      this.marketingBoardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('reset_checklist'),
      }),
    );
  };

  inNoteUpdate = (boardId: MarketingBoardId, newNote: MBNoteResponse | MBNoteId, type: 'insert' | 'update' | 'delete') => {
    const marketBoard = this.marketingBoardRepo.getItem(boardId)! as MarketingBoardResponse;
    let newNotes: MBNoteResponse[] = [];
    if (type === 'insert') {
      newNotes = [...marketBoard.notes, newNote as MBNoteResponse];
    } else if (type === 'update') {
      newNotes = marketBoard.notes.map(note => {
        if (note.id === (newNote as MBNoteResponse).id) {
          return newNote as MBNoteResponse;
        }
        return note;
      });
    } else {
      newNotes = R.filter(marketBoard.notes, n => n.id !== newNote);
    }

    newNotes = R.sortBy(newNotes, n => -n.created);
    this.marketingBoardRepo.updateOne({
      id: boardId,
      changes: () => ({ notes: newNotes }),
    });
  };

  setVendastaAccounts = (boardId: MarketingBoardId, id: string, name: string, objectId?: string) => {
    return OrganizationAPI.setVendastaAccounts(boardId, id, name).pipe(
      this.marketingBoardRepo.ops.updateOne(organization => ({
        id: boardId,
        changes: () => ({ organization }),
      })),
    );
  };

  updateStep = (
    inBoard: MarketingBoardResponse,
    subId: MBSubscriptionId,
    serviceId: MBServiceId,
    sectionId: string,
    updatedStep: MBServiceStepResponse,
    isSetup: boolean,
  ) => {
    const board = R.cloneDeep(inBoard);
    const subscription = R.find(board.subscriptions, s => s.id === subId)!;
    const service = R.find(subscription.services, s => s.id === serviceId)!;
    let section: MBServiceSectionResponse;
    let step: MBServiceStepResponse;
    if (isSetup) {
      section = R.find(service.setupChecklist.sections, s => s.ownId === sectionId)!;
      step = R.find(section.steps, s => s.id === updatedStep.id)!;
    } else {
      const sections = R.filter(
        R.flatMap(service.monthlyChecklists, m => m.sections),
        s => s.id === sectionId,
      )!;

      step = R.find(
        R.flatMap(sections, m => m.steps),
        s => s.id === updatedStep.id,
      )!;
    }

    step.isCompleted = updatedStep.isCompleted;
    step.completedAt = updatedStep.completedAt;
    step.completedBy = updatedStep.completedBy;
    step.feedback = updatedStep.feedback;
    step.notes = updatedStep.notes;
    this.marketingBoardRepo.updateOne({
      id: board.id,
      changes: () => board,
    });
  };

  getStep = (
    inBoard: MarketingBoardResponse,
    subId: MBSubscriptionId,
    serviceId: MBServiceId,
    sectionId: string,
    stepId: string,
    isSetup: boolean,
  ) => {
    const board = R.cloneDeep(inBoard);
    const subscription = R.find(board.subscriptions, s => s.id === subId)!;
    const service = R.find(subscription.services, s => s.id === serviceId)!;
    let section: MBServiceSectionResponse;
    if (isSetup) {
      section = R.find(service.setupChecklist.sections, s => s.ownId === sectionId)!;
      return R.find(section.steps, s => s.id === stepId)!;
    } else {
      const sections = R.filter(
        R.flatMap(service.monthlyChecklists, m => m.sections),
        s => s.id === sectionId,
      )!;

      return R.find(
        R.flatMap(sections, m => m.steps),
        s => s.id === stepId,
      )!;
    }
  };

  updateBoard = (boardId: MarketingBoardId, subId: MBSubscriptionId, serviceId: MBServiceId, sections: MBServiceSectionResponse[]) => {
    const board = this.marketingBoardRepo.getItem(boardId)! as MarketingBoardResponse;
    const subscriptions = board.subscriptions.map(sub => {
      if (sub.id === subId) {
        return {
          ...sub,
          services: sub.services.map(s => {
            if (s.id === serviceId) {
              const setupChecklist = { ...s.setupChecklist, sections: R.sortBy(sections, s => s.name) };
              return { ...s, setupChecklist };
            }
            return s;
          }),
        };
      }
      return sub;
    }) as MBSubscriptionResponse[];

    this.marketingBoardRepo.updateOne({
      id: board.id,
      changes: () => ({ subscriptions }),
    });
  };

  createSection = (boardId: MarketingBoardId, subId: MBSubscriptionId, serviceId: MBServiceId, name: string) => {
    return MarketingBoardAPI.createSection(subId, serviceId, name).pipe(
      tap(sections => {
        this.updateBoard(boardId, subId, serviceId, sections);
      }),
      handleMessage({
        type: requestMessage('create_template_section'),
      }),
    );
  };

  updateSection = (boardId: MarketingBoardId, subId: MBSubscriptionId, serviceId: MBServiceId, sectionId: string, name: string) => {
    return MarketingBoardAPI.updateSection(boardId, subId, serviceId, sectionId, name).pipe(
      tap(sections => {
        this.updateBoard(boardId, subId, serviceId, sections);
      }),
      handleMessage({
        type: requestMessage('update_template_section'),
      }),
    );
  };

  deleteSection = (boardId: MarketingBoardId, subId: MBSubscriptionId, serviceId: MBServiceId, sectionId: string) => {
    return showDeleteConfirmation('Delete Section', 'Do you really want to remove this Section?').pipe(
      switchMap(() =>
        MarketingBoardAPI.deleteSection(sectionId, subId, serviceId).pipe(
          tap(sections => {
            this.updateBoard(boardId, subId, serviceId, sections);
          }),
        ),
      ),
      handleMessage({
        type: requestMessage('delete_template_section'),
      }),
    );
  };

  sortSections = (boardId: MarketingBoardId, subId: MBSubscriptionId, serviceId: MBServiceId, sectionIds: string[]) => {
    return MarketingBoardAPI.sortSections(subId, serviceId, sectionIds).pipe(
      tap(sections => {
        this.updateBoard(boardId, subId, serviceId, sections);
      }),
      handleMessage({
        error: requestMessage('update_template_section_sort'),
      }),
    );
  };

  createStep = (
    boardId: MarketingBoardId,
    subId: MBSubscriptionId,
    serviceId: MBServiceId,
    sectionId: number,
    data: Partial<MBServiceStepResponse>,
  ) => {
    return MarketingBoardAPI.createStep(subId, serviceId, sectionId, data).pipe(
      tap(sections => {
        this.updateBoard(boardId, subId, serviceId, sections);
      }),
      handleMessage({
        type: requestMessage('create_template_section_step'),
      }),
    );
  };

  updateSetupStep = (
    boardId: MarketingBoardId,
    subId: MBSubscriptionId,
    serviceId: MBServiceId,
    sectionId: string,
    stepId: string,
    data: Partial<MBServiceStepResponse>,
  ) => {
    return MarketingBoardAPI.updateSetupStep(subId, serviceId, stepId, data).pipe(
      tap(sections => {
        this.updateBoard(boardId, subId, serviceId, sections);
      }),
      handleMessage({
        type: requestMessage('update_template_section_step'),
      }),
    );
  };

  deleteStep = (boardId: MarketingBoardId, subId: MBSubscriptionId, serviceId: MBServiceId, stepId: string) => {
    return showDeleteConfirmation('Delete Section', 'Do you really want to remove this Step?').pipe(
      switchMap(() =>
        MarketingBoardAPI.deleteSetupStep(stepId, subId, serviceId).pipe(
          tap(sections => {
            this.updateBoard(boardId, subId, serviceId, sections);
          }),
        ),
      ),
      handleMessage({
        type: requestMessage('delete_template_section'),
      }),
    );
  };

  sortSectionSteps = (boardId: MarketingBoardId, subId: MBSubscriptionId, serviceId: MBServiceId, sectionId: string, stepIds: string[]) => {
    return MarketingBoardAPI.sortSectionSteps(subId, serviceId, stepIds).pipe(
      tap(sections => {
        this.updateBoard(boardId, subId, serviceId, sections);
      }),
      handleMessage({
        error: requestMessage('update_template_steps_sort'),
      }),
    );
  };
}
